import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Typography,
	Box,
	Paper,
	Divider,
	Tabs,
	Tab,
	Pagination,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import { createTimeRecord } from '../../../../../services/shiftEndReportService'
import { useShiftEndContext } from '../../ShiftEndContext'
import WorkItemsTab from './AddRecordTabs/WorkItemsTab'
import ConstraintsTab from './AddRecordTabs/ConstraintsTab'
import NonProgressiblesTab from './AddRecordTabs/NonProgressiblesTab'
import {
	getForemanBacklogChildren,
	getForemanBacklogRoots,
	getForemanBacklogWorkItems,
	refreshForemanBacklogCache,
} from '../../../../../services/backlogService'
import { useLazyWorkItemTree } from '../../../../core/lazyTree/useLazyWorkItemTree'
import { useIndexedDB } from '../../../../hooks/useIndexedDB'
import { useProjectFeaturesContext } from '../../../../project/contexts/ProjectFeaturesContext'
import { getForemanConstraints } from '../../../../../services/workItemService'
import { getNonProgressibleWorkTypes } from '../../../../../services/projectService'
import { WorkItem } from '../../../../../types/Work/WorkItem'
import { Constraint } from '../../../../../types/Work/Constraint'
import { NonProgressibleWorkType } from '../../../../../types/Work/NonProgressible'
import { CircularProgress } from '@mui/material'
import { useDebounced } from '../../../../hooks/useDebounced'
import { searchWorkItemsHierarchy } from '../../../../../utils/WorkItemsTreeUtils'
import CommentPreview from '../CommentPreview'
import CommentDialog from '../CommentDialog'

type AddTasksDialogProps = {
	open: boolean
	onClose: () => void
	foremanId: number
	timesheetId: number
	beforeTimeRecordId?: number
}

type SelectedTask = {
	id: string // Unique identifier for this selection instance
	taskId: number
	name: string
	activityType: 'reworkTask' | 'workItemTask' | 'nonProgressible' | 'constraint'
	comment: string
}

type CommentTarget = {
	taskId: string
	comment: string
	activityLabel: string
	activityType: string
}

const ACTIVITY_TYPE_LABELS: Record<SelectedTask['activityType'], string> = {
	reworkTask: 'Rework',
	workItemTask: 'Work Item',
	nonProgressible: 'Non Progressible',
	constraint: 'Constraint',
}

const NON_PROGRESSIBLE_BATCH_SIZE = 10

const AddTasksDialog = ({
	open,
	onClose,
	foremanId,
	timesheetId,
	beforeTimeRecordId,
}: AddTasksDialogProps) => {
	const [loading, setLoading] = useState(false)
	const [dataLoading, setDataLoading] = useState(false)
	const [selectedTasks, setSelectedTasks] = useState<SelectedTask[]>([])
	const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null)
	const [activeTab, setActiveTab] = useState(0)
	const [workItems, setWorkItems] = useState<WorkItem[]>([])
	const [constraints, setConstraints] = useState<Constraint[]>([])
	const [nonProgressibleWorkTypes, setNonProgressibleWorkTypes] = useState<
		NonProgressibleWorkType[]
	>([])
	const [nonProgressibleTotalCount, setNonProgressibleTotalCount] = useState(0)
	const [nonProgressibleBatchNumber, setNonProgressibleBatchNumber] = useState(0)
	const [npListLoading, setNpListLoading] = useState(false)
	const { shiftDate } = useShiftEndContext()
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const foremanNameRef = useRef<string>('')
	const db = useIndexedDB()
	const showWorkItemsTab = isFeatureEnabled(14)
	const [workItemSearchText, setWorkItemSearchText] = useState('')
	const [constraintsSearchText, setConstraintsSearchText] = useState('')
	const [nonProgressiblesSearchText, setNonProgressiblesSearchText] = useState('')
	// Online, work items load a level at a time; offline, they come from the cached full tree.
	const [fullTree, setFullTree] = useState(false)
	const foremanIdRef = useRef(foremanId)
	foremanIdRef.current = foremanId
	const rootsRequest = useRef(0)
	const fetchChildren = useCallback(
		(workItemId: number) => getForemanBacklogChildren(foremanIdRef.current, workItemId),
		[],
	)
	const tree = useLazyWorkItemTree(fetchChildren)
	useEffect(() => {
		tree.reset()
		if (open && foremanId) {
			loadAllData()
			// Reset to first available tab when dialog opens
			setActiveTab(0)
		} else {
			rootsRequest.current++
			foremanNameRef.current = ''
			setWorkItemSearchText('')
			setConstraintsSearchText('')
			setNonProgressiblesSearchText('')
			setNonProgressibleBatchNumber(0)
			setNonProgressibleTotalCount(0)
			// Reset data when dialog closes
			setWorkItems([])
			setConstraints([])
			setNonProgressibleWorkTypes([])
		}
	}, [open, foremanId])

	useEffect(() => {
		// Reset to first available tab when feature flag changes
		if (open) {
			setActiveTab(0)
		}
	}, [showWorkItemsTab, open])

	const loadAllData = async () => {
		setDataLoading(true)
		const requestId = ++rootsRequest.current
		try {
			const [{ employee, fullTree: isFullTree }, constraintsResult] = await Promise.all([
				// A search typed just before the dialog last closed can still be set.
				getForemanBacklogWorkItems(foremanId, db, workItemSearchText),
				getForemanConstraints(foremanId),
			])
			if (!isFullTree && db) refreshForemanBacklogCache(foremanId, db)
			if (requestId !== rootsRequest.current) return

			foremanNameRef.current = employee.firstName + ' ' + employee.lastName
			setFullTree(isFullTree)
			setWorkItems(employee.workItems ?? [])
			setConstraints(constraintsResult)
		} catch (error) {
			console.error('Failed to load data:', error)
		} finally {
			setDataLoading(false)
		}
	}

	useEffect(() => {
		if (!open) {
			return
		}
		let cancelled = false
		;(async () => {
			setNpListLoading(true)
			try {
				const trimmed = nonProgressiblesSearchText.trim()
				const nonProgressiblesResult = await getNonProgressibleWorkTypes(false, {
					batchSize: NON_PROGRESSIBLE_BATCH_SIZE,
					batchNumber: nonProgressibleBatchNumber,
					...(trimmed ? { searchTerm: trimmed } : {}),
				})
				if (cancelled) {
					return
				}
				setNonProgressibleWorkTypes(nonProgressiblesResult.items ?? [])
				setNonProgressibleTotalCount(nonProgressiblesResult.totalCount)
			} catch (error) {
				console.error('Failed to load non-progressible work types:', error)
			} finally {
				if (!cancelled) {
					setNpListLoading(false)
				}
			}
		})()
		return () => {
			cancelled = true
		}
	}, [open, nonProgressibleBatchNumber, nonProgressiblesSearchText])

	const handleClose = () => {
		setSelectedTasks([])
		setCommentTarget(null)
		foremanNameRef.current = ''
		onClose()
	}

	const handleAddTask = (
		taskId: number,
		name: string,
		activityType: 'reworkTask' | 'workItemTask' | 'nonProgressible' | 'constraint',
	) => {
		const uniqueId = `${taskId}-${Date.now()}-${Math.random()}`
		setSelectedTasks((prev) => [
			...prev,
			{ id: uniqueId, taskId, name, activityType, comment: '' },
		])
	}

	const handleRemoveTask = (id: string) => {
		setSelectedTasks((prev) => prev.filter((task) => task.id !== id))
	}

	const handleCommentChange = (id: string, comment: string) => {
		setSelectedTasks((prev) =>
			prev.map((task) => (task.id === id ? { ...task, comment } : task)),
		)
	}

	const openCommentDialog = (task: SelectedTask) => {
		const pathParts = task.name.split(' > ')
		const taskName = pathParts[pathParts.length - 1]
		setCommentTarget({
			taskId: task.id,
			comment: task.comment,
			activityLabel: taskName,
			activityType: ACTIVITY_TYPE_LABELS[task.activityType],
		})
	}

	const handleCreateTimeRecord = async () => {
		setLoading(true)
		try {
			await createTimeRecord(
				timesheetId,
				selectedTasks.map((task) => ({
					taskId: task.taskId,
					activityType: task.activityType,
					comment: task.comment.trim() || undefined,
				})),
				shiftDate,
				beforeTimeRecordId,
			)
			handleClose()
		} finally {
			setLoading(false)
		}
	}

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue)
	}
	
	const debouncedSetWorkItemsSearch = useDebounced((text: string) => setWorkItemSearchText(text), 500)
	const handleSearchTextChange = (text: string) => debouncedSetWorkItemsSearch(text)

	// The first load already applies the current search, so this only runs on edits.
	useEffect(() => {
		if (!open || fullTree) return
		const requestId = ++rootsRequest.current
		getForemanBacklogRoots(foremanId, workItemSearchText)
			.then((result) => {
				if (requestId !== rootsRequest.current) return
				setWorkItems(result?.workItems ?? [])
			})
			.catch(() => undefined)
	}, [workItemSearchText])

	const filteredWorkItems = useMemo(() => {
		// Online, the server already filtered the top level by the search.
		if (!fullTree || !workItemSearchText.trim()) return workItems
		return searchWorkItemsHierarchy(workItems, workItemSearchText)
	}, [workItems, workItemSearchText, fullTree])

	// Work items that leave the top level on a new search come back closed.
	const listedWorkItems = useRef<WorkItem[]>([])
	useEffect(() => {
		const listedIds = new Set(filteredWorkItems.map((w) => w.id))
		tree.closeUnder(listedWorkItems.current.filter((w) => !listedIds.has(w.id)))
		listedWorkItems.current = filteredWorkItems
	}, [filteredWorkItems])

	const debouncedSetConstraintsSearch = useDebounced(
		(text: string) => setConstraintsSearchText(text),
		500,
	)
	const handleConstraintsSearchTextChange = (text: string) => debouncedSetConstraintsSearch(text)

	const debouncedSetNonProgressiblesSearch = useDebounced((text: string) => {
		setNonProgressiblesSearchText(text)
		setNonProgressibleBatchNumber(0)
	}, 600)
	const handleNonProgressiblesSearchTextChange = (text: string) =>
		debouncedSetNonProgressiblesSearch(text)

	const filteredConstraints = useMemo(() => {
		const trimmed = constraintsSearchText.trim()
		if (!trimmed) return constraints
		const lower = trimmed.toLowerCase()
		return constraints.filter(
			(c) =>
				c.comments?.toLowerCase().includes(lower) ||
				c.workItemName?.toLowerCase().includes(lower),
		)
	}, [constraints, constraintsSearchText])

	const isNonProgressiblesTab = showWorkItemsTab ? activeTab === 2 : activeTab === 1
	const nonProgressiblePageCount = Math.ceil(
		nonProgressibleTotalCount / NON_PROGRESSIBLE_BATCH_SIZE,
	)

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth='lg'>
			<DialogTitle>
				Add Tasks from {foremanNameRef.current}&apos;s backlog
				<IconButton
					onClick={handleClose}
					sx={(theme) => ({
						position: 'absolute',
						right: 8,
						top: 8,
						color: theme.palette.grey[500],
					})}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{dataLoading ? (
					<Box
						sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ mt: 2, display: 'flex', gap: 2, height: '60vh' }}>
						{/* Left Panel - Tabs */}
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
							<Tabs
								value={activeTab}
								onChange={handleTabChange}
								sx={{ borderBottom: 1, borderColor: 'divider' }}
							>
								{showWorkItemsTab && <Tab label='WorkItems' />}
								<Tab label='Constraints' />
								<Tab label='Non Progressibles' />
							</Tabs>
							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									display: 'flex',
									flexDirection: 'column',
									overflow: 'hidden',
								}}
							>
								<Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
									{/* WorkItems Tab Panel */}
									{showWorkItemsTab && activeTab === 0 && (
										<WorkItemsTab
											workItems={filteredWorkItems}
											tree={tree}
											onAddTask={handleAddTask}
											searchText={workItemSearchText}
											handleSearchTextChange={handleSearchTextChange}
										/>
									)}
									{/* Constraints Tab Panel */}
									{showWorkItemsTab
										? activeTab === 1 && (
												<ConstraintsTab
													constraints={filteredConstraints}
													onAddTask={handleAddTask}
													searchText={constraintsSearchText}
													handleSearchTextChange={handleConstraintsSearchTextChange}
												/>
										  )
										: activeTab === 0 && (
												<ConstraintsTab
													constraints={filteredConstraints}
													onAddTask={handleAddTask}
													searchText={constraintsSearchText}
													handleSearchTextChange={handleConstraintsSearchTextChange}
												/>
										  )}
									{/* Non Progressibles Tab Panel */}
									{showWorkItemsTab
										? activeTab === 2 && (
												<NonProgressiblesTab
													nonProgressibleWorkTypes={nonProgressibleWorkTypes}
													onAddTask={handleAddTask}
													searchText={nonProgressiblesSearchText}
													handleSearchTextChange={handleNonProgressiblesSearchTextChange}
												/>
										  )
										: activeTab === 1 && (
												<NonProgressiblesTab
													nonProgressibleWorkTypes={nonProgressibleWorkTypes}
													onAddTask={handleAddTask}
													searchText={nonProgressiblesSearchText}
													handleSearchTextChange={handleNonProgressiblesSearchTextChange}
												/>
										  )}
								</Box>
								{isNonProgressiblesTab && nonProgressiblePageCount > 1 && (
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											py: 1,
											px: 1,
											borderTop: 1,
											borderColor: 'divider',
											flexShrink: 0,
										}}
									>
										<Pagination
											color='primary'
											size='small'
											count={nonProgressiblePageCount}
											page={nonProgressibleBatchNumber + 1}
											onChange={(_, page) => setNonProgressibleBatchNumber(page - 1)}
											disabled={npListLoading}
										/>
									</Box>
								)}
							</Box>
						</Box>

						{/* Right Panel - Selected Tasks */}
						<Divider orientation='vertical' flexItem />
						<Paper
							sx={{
								width: '380px',
								display: 'flex',
								flexDirection: 'column',
								maxHeight: '100%',
								overflow: 'hidden',
							}}
							elevation={2}
						>
							<Box
								sx={{
									p: 2,
									borderBottom: 1,
									borderColor: 'divider',
									backgroundColor: 'primary.main',
									color: 'primary.contrastText',
								}}
							>
								<Typography variant='h6' sx={{ fontWeight: 600 }}>
									Selected Tasks ({selectedTasks.length})
								</Typography>
							</Box>
							<Box sx={{ flex: 1, overflowY: 'auto' }}>
								{selectedTasks.length === 0 ? (
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											height: '100%',
											p: 3,
										}}
									>
										<Typography variant='body2' color='text.secondary' align='center'>
											No tasks selected.
											<br />
											Click the + icon to add tasks.
										</Typography>
									</Box>
								) : (
									<Box sx={{ p: 0 }}>
										{selectedTasks.map((task, index) => {
											const pathParts = task.name.split(' > ')
											const workItems = pathParts.slice(0, -1)
											const taskName = pathParts[pathParts.length - 1]

											return (
												<React.Fragment key={task.id}>
													<Box
														sx={{
															display: 'flex',
															alignItems: 'flex-start',
															p: 2,
															'&:hover': {
																backgroundColor: 'action.hover',
															},
														}}
													>
														<Box sx={{ flex: 1, minWidth: 0 }}>
															{workItems.map((workItem, wiIndex) => (
																<Typography
																	key={wiIndex}
																	variant='body2'
																	sx={{
																		fontWeight: 500,
																		mb: 0.5,
																		wordBreak: 'break-word',
																	}}
																>
																	{wiIndex + 1}. {workItem}
																</Typography>
															))}
															<Typography
																variant='body2'
																sx={{
																	fontWeight: 500,
																	mt: 0.5,
																	wordBreak: 'break-word',
																}}
															>
																{taskName}
															</Typography>
															<Typography
																variant='caption'
																color='text.secondary'
																sx={{ display: 'block', mt: 0.5, mb: 1 }}
															>
																Task ID: {task.taskId}
															</Typography>
															<CommentPreview
																comment={task.comment}
																canEdit
																onClick={() => openCommentDialog(task)}
															/>
														</Box>
														<IconButton
															aria-label='remove task'
															onClick={() => handleRemoveTask(task.id)}
															size='small'
															color='error'
															sx={{ ml: 1 }}
														>
															<DeleteIcon fontSize='small' />
														</IconButton>
													</Box>
													{index < selectedTasks.length - 1 && <Divider />}
												</React.Fragment>
											)
										})}
									</Box>
								)}
							</Box>
						</Paper>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button color='warning' variant='contained' onClick={handleClose}>
					Cancel
				</Button>
				<Button
					color='success'
					variant='contained'
					onClick={handleCreateTimeRecord}
					disabled={loading || selectedTasks.length === 0}
				>
					Add Selected Tasks ({selectedTasks.length})
				</Button>
			</DialogActions>
			<CommentDialog
				open={commentTarget !== null}
				onClose={() => setCommentTarget(null)}
				onSave={async (comment) => {
					if (!commentTarget) return
					handleCommentChange(commentTarget.taskId, comment)
				}}
				initialComment={commentTarget?.comment ?? ''}
				activityLabel={commentTarget?.activityLabel ?? ''}
				activityType={commentTarget?.activityType ?? ''}
				canEdit
			/>
		</Dialog>
	)
}

export default AddTasksDialog
